"use client";

import { SSpecificationDto } from "@/__generated__/graphql";
import CloseCircleBoldIcon from "@/app/_components/Icons/CloseCircleBoldIcon";
import TickCircleBoldIcon from "@/app/_components/Icons/TickCircleBoldIcon";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Button, Icon, Typography } from "@mui/material";
import { memo, useState } from "react";

const PREVIEW_COUNT = 8;

function ProductMidSectionSpecifications({
  specifications,
  alwaysExpanded = false,
  sheet = false,
}: {
  specifications: SSpecificationDto[];
  alwaysExpanded?: boolean;
  /** Compact two-column rows for the mobile bottom sheet. */
  sheet?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const showAll = alwaysExpanded || expanded;
  const canToggle = !alwaysExpanded && specifications.length > PREVIEW_COUNT;

  return (
    <Box data-product-specifications>
      <Box
        component="dl"
        data-specifications-list
        sx={{
          m: 0,
          display: "flex",
          flexDirection: "column",
          gap: sheet ? 0 : 0.5,
          borderRadius: sheet ? 2 : 0,
          overflow: sheet ? "hidden" : "visible",
          border: sheet ? "1px solid" : "none",
          borderColor: sheet ? "grey.100" : "transparent",
        }}
      >
        {specifications.map((specification, index) => {
          const hidden = canToggle && !showAll && index >= PREVIEW_COUNT;
          return (
            <Box
              key={index}
              data-specification-row
              data-specification-type={specification.type}
              data-specification-index={index}
              sx={{
                display: hidden ? "none" : "grid",
                gridTemplateColumns: sheet
                  ? "minmax(112px, 38%) minmax(0, 1fr)"
                  : { xs: "1fr", sm: "minmax(140px, 32%) minmax(0, 1fr)" },
                gap: sheet ? 1.25 : { xs: 0.35, sm: 2 },
                alignItems: "center",
                px: sheet ? 1.5 : { xs: 1.25, sm: 1.75 },
                py: sheet ? 1.35 : { xs: 1.15, sm: 1.35 },
                borderRadius: sheet ? 0 : 2,
                borderBottom: sheet && index < specifications.length - 1 ? "1px solid" : "none",
                borderColor: "grey.100",
                bgcolor: index % 2 === 0 ? (sheet ? "grey.50" : "grey.A100") : sheet ? "background.paper" : "transparent",
              }}
            >
              <Typography
                component="dt"
                data-specification-title
                variant="body2"
                color="grey.600"
                sx={{ lineHeight: 1.7, fontSize: sheet ? "0.8125rem" : undefined }}
              >
                {specification.title}
              </Typography>
              <Box component="dd" data-specification-value sx={{ m: 0, minWidth: 0 }}>
                {specification.type === "Boolean" && (
                  <Icon color={specification.checked ? "success" : "error"} data-boolean-value={specification.checked}>
                    {specification.checked ? <TickCircleBoldIcon /> : <CloseCircleBoldIcon />}
                  </Icon>
                )}
                {specification.type === "Text" && (
                  <Typography
                    data-text-value
                    variant="body1"
                    color="grey.900"
                    sx={{
                      lineHeight: 1.8,
                      overflowWrap: "anywhere",
                      fontSize: sheet ? "0.9rem" : undefined,
                      fontWeight: sheet ? 600 : undefined,
                    }}
                  >
                    {specification.text}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {canToggle && (
        <Button
          variant="text"
          color="inherit"
          onClick={() => setExpanded((open) => !open)}
          endIcon={showAll ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{ mt: 1, px: 0.5, color: "grey.800" }}
        >
          {showAll ? "نمایش کمتر" : `نمایش همه مشخصات (${specifications.length})`}
        </Button>
      )}
    </Box>
  );
}

export default memo(ProductMidSectionSpecifications);
